import { useEffect, useMemo, useRef, useState } from 'react'
import { Align } from 'components/shared/Box'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import { Wrapper } from './styles'
import { DefaultTheme } from 'styled-components'
import { IconVariant } from 'components/shared/Icon/types'
import { Color } from 'styles/types'
import { Align as TextAlign } from 'components/shared/Text'

interface InfoProps {
    children: React.ReactNode
    color: 'error' | 'success' | 'info' | 'warning' | 'info_warning'
    iconColor?: Color
    hasIcon?: boolean
    hasNoPadding?: boolean
    hasSmallFont?: boolean
    hasBorder?: boolean
    hasNoBackground?: boolean
    hasNoShadow?: boolean
    align?: Align | [Align, Align]
    textAlign?: TextAlign | [TextAlign, TextAlign]
    theme?: DefaultTheme
    className?: string
    variantType?: IconVariant
    isFullWidthContent?: boolean
    borderColor?: Color
    backgroundColor?: Color
    hasDarkMode?: boolean
}

const Info = ({
    children,
    color,
    iconColor,
    hasIcon = true,
    hasSmallFont = false,
    hasNoPadding = false,
    align,
    textAlign,
    hasBorder,
    hasNoBackground = false,
    hasNoShadow = false,
    theme,
    className,
    variantType,
    isFullWidthContent,
    borderColor,
    backgroundColor,
    hasDarkMode,
}: InfoProps) => {
    const textRef = useRef<HTMLParagraphElement>(null)

    const [localAlign, setLocalAlign] = useState<
        Align | [Align, Align] | undefined
    >('flex-start')

    const averageLineHeight = 24
    const isDarkModeActive = theme?.mode === 'dark' || hasDarkMode

    const getIconColor = () => {
        if (iconColor) return iconColor
        if (color === 'warning' && isDarkModeActive) return 'primary_300'
        if (color === 'info_warning' || isDarkModeActive) return 'primary_500'
        return color
    }
    const calculatedIconColor = getIconColor()

    const textAlignCalculated = useMemo(() => {
        if (textAlign) return textAlign
        return hasIcon ? 'left' : 'center'
    }, [textAlign, hasIcon])

    useEffect(() => {
        // in messages without declared alignment,
        // align one-line texts as 'centered'
        if (
            textRef.current &&
            textRef.current.offsetHeight <= averageLineHeight &&
            !align
        ) {
            setLocalAlign('center')
        }
    }, [])

    return (
        <Wrapper
            direction="row"
            align={localAlign}
            gap={12}
            background={backgroundColor ?? `${color}_bg`}
            hasSmallFont={hasSmallFont}
            justify={hasIcon ? 'flex-start' : 'center'}
            isFullWidth
            hasBorder={hasBorder}
            hasNoPadding={hasNoPadding}
            hasNoBackground={hasNoBackground}
            hasNoShadow={hasNoShadow}
            theme={theme}
            className={className}
            borderColor={borderColor}
        >
            {hasIcon && (
                <Icon
                    variant={variantType ?? 'tooltip'}
                    color={calculatedIconColor}
                />
            )}
            <Text
                variant={hasSmallFont ? 'bodyExtraSmall' : 'bodySmall'}
                color={color}
                align={textAlignCalculated}
                as="div"
                style={isFullWidthContent ? { width: '100%' } : {}}
                ref={textRef}
            >
                {children}
            </Text>
        </Wrapper>
    )
}

export default Info
